import React, { useState } from 'react';
import type { KeyboardEvent } from 'react';
import {
  Table,
  Input,
  Row,
  Col,
  FormGroup,
  InputGroup,
  InputGroupText,
  Pagination,
  PaginationItem,
  PaginationLink,
} from 'reactstrap';
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
} from '@tanstack/react-table';
import type { PaginationState, SortDirection } from '@tanstack/react-table';

// aria-sort values for the three states @tanstack/react-table reports
// (`getIsSorted()` returns 'asc', 'desc' or false).
const ARIA_SORT: Record<SortDirection, 'ascending' | 'descending'> = { asc: 'ascending', desc: 'descending' };

export type TableWrapperProps<TData> = {
  pagesToDisplay?: number;
  data?: TData[];
  // Not `ColumnDef<TData, any>[]`: naming that type here makes tsc measure the
  // variance of @tanstack's column types before it reaches ProductTable.jsx, whose
  // untyped `createColumnHelper()` columns then fail at its own useReactTable call
  // (TS2322, ProductTable.jsx:73). Narrow this when ProductTable is converted.
  columns: any[];
  searchLabel?: string;
  searchId?: string;
  manualPagination?: boolean;
  rowCount?: number;
  pagination?: PaginationState;
  onPaginationChange?: (p: PaginationState) => void;
  searchValue?: string;
  onSearchChange?: (text: string) => void;
};

const TableWrapper = <TData,>({
  pagesToDisplay = 20,
  data = [],
  columns,
  searchLabel = 'Search',
  // SCRUM-1159: a screen with two tables would otherwise carry this id twice.
  // The default keeps every single-table call site (and the selectors pointing
  // at them) exactly as it was; screens with two tables pass their own.
  searchId = 'searchInput',
  // SCRUM-1811: opt-in server-side mode. `data` is then ONE page the caller
  // fetched; the caller owns `pagination` ({ pageIndex, pageSize }) and the
  // search text, and `rowCount` is the server's total. Off by default, so every
  // other table keeps paging and searching in the browser exactly as before.
  manualPagination = false,
  rowCount = 0,
  pagination = {
    pageIndex: 0,
    pageSize: 10,
  },
  onPaginationChange = undefined,
  searchValue = '',
  onSearchChange = undefined,
}: TableWrapperProps<TData>) => {
  const [globalFilter, setGlobalFilter] = useState('');

  const table = useReactTable({
    data,
    columns,
    state: manualPagination
      ? { globalFilter: '', pagination }
      : { globalFilter },
    onGlobalFilterChange: setGlobalFilter,
    ...(manualPagination && {
      manualPagination: true,
      manualFiltering: true,
      rowCount,
      onPaginationChange: updater =>
        onPaginationChange?.(
          typeof updater === 'function' ? updater(pagination) : updater
        ),
    }),
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const { pageIndex, pageSize } = table.getState().pagination;
  const pageCount = table.getPageCount();
  const totalRows = manualPagination ? rowCount : data.length;
  const currentPageStart = pageIndex * pageSize + 1;
  const currentPageEnd = Math.min((pageIndex + 1) * pageSize, totalRows);

  // Function to handle sorting when clicking on a column header
  const handleSort = (columnId: string) => {
    const column = table.getColumn(columnId);
    column?.toggleSorting();
  };

  // Keyboard equivalent of the header click. Space would scroll the page.
  const handleSortKeyDown = (event: KeyboardEvent, columnId: string) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleSort(columnId);
    }
  };

  const halfPagesToDisplay = Math.floor(pagesToDisplay / 2);
  let startPage = Math.max(0, pageIndex - halfPagesToDisplay);
  let endPage = Math.min(pageCount - 1, startPage + pagesToDisplay - 1);

  if (endPage - startPage < pagesToDisplay - 1) {
    startPage = Math.max(0, endPage - pagesToDisplay + 1);
  }

  return (
    <>
      <Row className="ml-2 mt-3">
        <FormGroup>
          <InputGroup className="input-group-alternative input-group-merge">
            <InputGroupText>
              <i className="fas fa-search" />
            </InputGroupText>
            <Input
              id={searchId}
              aria-label={searchLabel}
              value={(manualPagination ? searchValue : globalFilter) || ''}
              onChange={e =>
                manualPagination
                  ? onSearchChange?.(e.target.value)
                  : setGlobalFilter(e.target.value)
              }
              placeholder="Type to search..."
            />
          </InputGroup>
        </FormGroup>
      </Row>

      <Table className="align-items-center table-flush" responsive striped>
        <thead className="thead-light">
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <th
                  key={header.id}
                  aria-sort={ARIA_SORT[header.column.getIsSorted() as SortDirection] || 'none'}
                  tabIndex={0}
                  onClick={() => handleSort(header.id)}
                  onKeyDown={e => handleSortKeyDown(e, header.id)}
                  style={{ cursor: 'pointer' }}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}

                  {header.column.getIsSorted() ? (
                      <i className={`fa ${header.column.getIsSorted() === 'asc' ? 'fa-arrow-up' : 'fa-arrow-down'} ml-2 `}/>
                  ) : (
                    <i className="fa fa-sort ml-2" />
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.length > 0 ? (
            table.getRowModel().rows.map(row => (
              <tr key={row.id}>
                {row.getVisibleCells().map(cell => (
                  <td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={columns.length}>No data available</td>
            </tr>
          )}
        </tbody>
      </Table>

      <Pagination
        aria-label="Table pagination"
        className="pagination justify-content-center mt-3"
      >
        <PaginationItem disabled={!table.getCanPreviousPage()}>
          <PaginationLink aria-label="First page" onClick={() => table.setPageIndex(0)}>
            <i className={`fa fa-angle-double-left ${!table.getCanPreviousPage() ? 'icon-disabled' : ''}`} />
          </PaginationLink>
        </PaginationItem>

        <PaginationItem disabled={!table.getCanPreviousPage()}>
          <PaginationLink aria-label="Previous page" onClick={() => table.previousPage()}>
            <i className={`fa fa-angle-left ${!table.getCanPreviousPage() ? 'icon-disabled' : ''}`} />
          </PaginationLink>
        </PaginationItem>

        {Array.from({ length: endPage - startPage + 1 }).map((_, index) => {
          const page = startPage + index;
          return (
            <PaginationItem key={page} active={page === pageIndex}>
              <PaginationLink
                aria-label={`Page ${page + 1}`}
                aria-current={page === pageIndex ? 'page' : undefined}
                onClick={() => table.setPageIndex(page)}
              >
                {page + 1}
              </PaginationLink>
            </PaginationItem>
          );
        })}

        <PaginationItem disabled={!table.getCanNextPage()}>
          <PaginationLink aria-label="Next page" onClick={() => table.nextPage()}>
            <i className={`fa fa-angle-right ${!table.getCanNextPage() ? 'icon-disabled' : ''}`} />
          </PaginationLink>
        </PaginationItem>

        <PaginationItem disabled={!table.getCanNextPage()}>
          <PaginationLink aria-label="Last page" onClick={() => table.setPageIndex(pageCount - 1)}>
            <i className={`fa fa-angle-double-right ${!table.getCanNextPage() ? 'icon-disabled' : ''}`} />
          </PaginationLink>
        </PaginationItem>
      </Pagination>

      <Row className="mt-3">
        <Col sm="auto">
          <div className="d-flex align-items-center">
            <span className="mr-2">Show</span>

            <Input
              type="select"
              aria-label="Rows per page"
              value={pageSize}
              onChange={e => table.setPageSize(Number(e.target.value))}
              className="form-control form-control-sm w-auto"
            >
              {[10, 25, 50, 100].map(size => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </Input>

            <span className="ml-2">
              entries. Showing rows {currentPageStart} to {currentPageEnd} of {totalRows} entries.
            </span>
          </div>
        </Col>
      </Row>
    </>
  );
};

export default TableWrapper;